/**
 * AMICE Travel - Shopify Theme JavaScript
 * Version: 1.0.0
 *
 * Vanilla JS, no frameworks. Loaded as a single script in the theme layout.
 * All modules are self-contained IIFEs attached to a global AMICE namespace.
 */

(function () {
  'use strict';

  /* ==========================================================================
     0. GLOBAL NAMESPACE & STATE
     ========================================================================== */

  window.AMICE = window.AMICE || {};

  const STATE = {
    scrollY: 0,
    headerHeight: 0,
    isMobile: window.innerWidth < 768,
    isTablet: window.innerWidth >= 768 && window.innerWidth < 1024,
    cartOpen: false,
    searchOpen: false,
    mobileMenuOpen: false,
  };

  /* ==========================================================================
     1. UTILITY FUNCTIONS
     ========================================================================== */

  const Utils = (function () {

    function formatMoney(cents, format) {
      if (typeof cents === 'string') cents = cents.replace('.', '');
      cents = parseInt(cents, 10) || 0;

      var defaultFormat = window.Shopify && window.Shopify.money_format
        ? window.Shopify.money_format
        : '&euro;{{amount_with_comma_separator}}';
      format = format || defaultFormat;

      var value = '';
      var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
      var formatString = format;

      function addCommas(n) {
        return n.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
      }

      switch (formatString.match(placeholderRegex)[1]) {
        case 'amount':
          value = (cents / 100).toFixed(2);
          formatString = formatString.replace(placeholderRegex, value);
          break;
        case 'amount_no_decimals':
          value = Math.round(cents / 100);
          formatString = formatString.replace(placeholderRegex, value);
          break;
        case 'amount_with_comma_separator':
          value = (cents / 100).toFixed(2);
          value = value.replace('.', ',');
          value = value.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
          formatString = formatString.replace(placeholderRegex, value);
          break;
        case 'amount_no_decimals_with_comma_separator':
          value = Math.round(cents / 100);
          value = addCommas(value);
          formatString = formatString.replace(placeholderRegex, value);
          break;
        case 'amount_with_space_separator':
          value = (cents / 100).toFixed(2);
          value = value.replace('.', ',');
          value = value.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
          formatString = formatString.replace(placeholderRegex, value);
          break;
        case 'amount_no_decimals_with_space_separator':
          value = Math.round(cents / 100);
          value = value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
          formatString = formatString.replace(placeholderRegex, value);
          break;
        case 'amount_with_apostrophe_separator':
          value = (cents / 100).toFixed(2);
          value = value.replace('.', ',');
          value = value.replace(/\B(?=(\d{3})+(?!\d))/g, "'");
          formatString = formatString.replace(placeholderRegex, value);
          break;
        default:
          value = (cents / 100).toFixed(2);
          formatString = formatString.replace(placeholderRegex, value);
      }

      return formatString;
    }

    function debounce(fn, delay) {
      var timer;
      return function () {
        var context = this;
        var args = arguments;
        clearTimeout(timer);
        timer = setTimeout(function () {
          fn.apply(context, args);
        }, delay);
      };
    }

    function throttle(fn, limit) {
      var waiting = false;
      return function () {
        if (!waiting) {
          fn.apply(this, arguments);
          waiting = true;
          setTimeout(function () {
            waiting = false;
          }, limit);
        }
      };
    }

    function trapFocus(container) {
      var focusableSelectors = [
        'a[href]',
        'button:not([disabled])',
        'input:not([disabled]):not([type="hidden"])',
        'select:not([disabled])',
        'textarea:not([disabled])',
        '[tabindex]:not([tabindex="-1"])',
      ];
      var focusable = container.querySelectorAll(focusableSelectors.join(','));
      var firstFocusable = focusable[0];
      var lastFocusable = focusable[focusable.length - 1];

      function handleKeyDown(e) {
        if (e.key !== 'Tab') return;

        if (e.shiftKey) {
          if (document.activeElement === firstFocusable) {
            e.preventDefault();
            lastFocusable.focus();
          }
        } else {
          if (document.activeElement === lastFocusable) {
            e.preventDefault();
            firstFocusable.focus();
          }
        }
      }

      container.addEventListener('keydown', handleKeyDown);

      if (firstFocusable) {
        firstFocusable.focus();
      }

      return {
        release: function () {
          container.removeEventListener('keydown', handleKeyDown);
        },
      };
    }

    function fetchSection(sectionId, url) {
      var fetchUrl = url || window.location.pathname;
      var separator = fetchUrl.indexOf('?') !== -1 ? '&' : '?';
      fetchUrl += separator + 'section_id=' + sectionId;

      return fetch(fetchUrl)
        .then(function (response) {
          if (!response.ok) throw new Error('Section fetch failed: ' + response.status);
          return response.text();
        });
    }

    function serializeForm(form) {
      var data = {};
      var formData = new FormData(form);
      formData.forEach(function (value, key) {
        if (data[key]) {
          if (!Array.isArray(data[key])) {
            data[key] = [data[key]];
          }
          data[key].push(value);
        } else {
          data[key] = value;
        }
      });
      return data;
    }

    function scrollTo(target, offset) {
      var el = typeof target === 'string' ? document.querySelector(target) : target;
      if (!el) return;
      var top = el.getBoundingClientRect().top + window.pageYOffset - (offset || 0);
      window.scrollTo({ top: top, behavior: 'smooth' });
    }

    function copyToClipboard(text) {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        return navigator.clipboard.writeText(text);
      }
      var textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.style.position = 'fixed';
      textarea.style.left = '-9999px';
      document.body.appendChild(textarea);
      textarea.select();
      try {
        document.execCommand('copy');
      } catch (err) {
        // Silently fail
      }
      document.body.removeChild(textarea);
      return Promise.resolve();
    }

    function emit(name, detail) {
      document.dispatchEvent(new CustomEvent('amice:' + name, { detail: detail, bubbles: true }));
    }

    function on(name, handler) {
      document.addEventListener('amice:' + name, handler);
    }

    return {
      formatMoney: formatMoney,
      debounce: debounce,
      throttle: throttle,
      trapFocus: trapFocus,
      fetchSection: fetchSection,
      serializeForm: serializeForm,
      scrollTo: scrollTo,
      copyToClipboard: copyToClipboard,
      emit: emit,
      on: on,
    };
  })();

  window.AMICE.Utils = Utils;


  /* ==========================================================================
     2. SHOPIFY CART API
     ========================================================================== */

  const CartAPI = (function () {

    var CART_ENDPOINT = '/cart.js';
    var ADD_ENDPOINT = '/cart/add.js';
    var CHANGE_ENDPOINT = '/cart/change.js';
    var UPDATE_ENDPOINT = '/cart/update.js';
    var CLEAR_ENDPOINT = '/cart/clear.js';

    var headers = {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    };

    function getCart() {
      return fetch(CART_ENDPOINT, { credentials: 'same-origin' })
        .then(function (res) { return res.json(); });
    }

    function addItem(variantId, quantity, properties) {
      var body = {
        items: [{
          id: variantId,
          quantity: quantity || 1,
        }],
      };
      if (properties) {
        body.items[0].properties = properties;
      }
      return fetch(ADD_ENDPOINT, {
        method: 'POST',
        headers: headers,
        credentials: 'same-origin',
        body: JSON.stringify(body),
      })
        .then(function (res) {
          if (!res.ok) return res.json().then(function (err) { throw err; });
          return res.json();
        })
        .then(function (data) {
          Utils.emit('cart:added', data);
          return getCart();
        });
    }

    function changeItem(key, quantity) {
      return fetch(CHANGE_ENDPOINT, {
        method: 'POST',
        headers: headers,
        credentials: 'same-origin',
        body: JSON.stringify({ id: key, quantity: quantity }),
      })
        .then(function (res) {
          if (!res.ok) return res.json().then(function (err) { throw err; });
          return res.json();
        })
        .then(function (cart) {
          Utils.emit('cart:changed', cart);
          return cart;
        });
    }

    function removeItem(key) {
      return changeItem(key, 0);
    }

    function updateCart(updates) {
      return fetch(UPDATE_ENDPOINT, {
        method: 'POST',
        headers: headers,
        credentials: 'same-origin',
        body: JSON.stringify({ updates: updates }),
      })
        .then(function (res) { return res.json(); })
        .then(function (cart) {
          Utils.emit('cart:changed', cart);
          return cart;
        });
    }

    function clearCart() {
      return fetch(CLEAR_ENDPOINT, {
        method: 'POST',
        headers: headers,
        credentials: 'same-origin',
      })
        .then(function (res) { return res.json(); })
        .then(function (cart) {
          Utils.emit('cart:changed', cart);
          return cart;
        });
    }

    return {
      getCart: getCart,
      addItem: addItem,
      changeItem: changeItem,
      removeItem: removeItem,
      updateCart: updateCart,
      clearCart: clearCart,
    };
  })();

  window.AMICE.CartAPI = CartAPI;

  const AMICE = window.AMICE;

  /* ==========================================================================
     3. DESIGN MODULES
     ========================================================================== */

  const $ = (s, c = document) => c.querySelector(s);
  const $$ = (s, c = document) => Array.from(c.querySelectorAll(s));
  const money = (cents) => Utils.formatMoney(cents, window.AMICE.moneyFormat);

  /* Header shadow on scroll */
  AMICE.Header = function () {
    const header = $("[data-header]");
    if (!header) return;
    const onScroll = () => header.classList.toggle("header--scrolled", window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
  };

  /* Full-screen MENU overlay (mobile) */
  AMICE.Menu = function () {
    const menu = $("[data-menu]");
    if (!menu) return;
    const set = (v) => { menu.dataset.open = v ? "true" : "false"; document.body.style.overflow = v ? "hidden" : ""; };
    $$("[data-menu-open]").forEach((b) => b.addEventListener("click", (e) => { e.preventDefault(); set(true); }));
    $$("[data-menu-close]").forEach((b) => b.addEventListener("click", () => set(false)));
    $$("a", menu).forEach((a) => a.addEventListener("click", () => set(false)));
    document.addEventListener("keydown", (e) => { if (e.key === "Escape") set(false); });
    const items = $$("[data-menu-item]", menu);
    const setItem = (item, open) => {
      item.dataset.open = open ? "true" : "false";
      const btn = $("[data-menu-toggle]", item);
      if (btn) btn.setAttribute("aria-expanded", String(open));
    };
    items.forEach((item) => {
      const btn = $("[data-menu-toggle]", item);
      btn && btn.addEventListener("click", () => {
        const open = item.dataset.open === "true";
        items.forEach((i) => setItem(i, false));
        setItem(item, !open);
      });
    });
  };

  /* Mega menu: hovering a colour swatch previews it in the feature slot */
  AMICE.MegaSwatchHover = function () {
    $$("[data-mega-feature]").forEach((feature) => {
      const img = $("img", feature);
      if (!img) return;
      const original = { src: img.src, srcset: img.getAttribute("srcset"), sizes: img.getAttribute("sizes") };
      const mega = feature.closest(".megamenu, .nav__item") || document;
      const swatches = $$("[data-hover-img]", mega);
      if (!swatches.length) return;
      const swap = (url) => { img.removeAttribute("srcset"); img.removeAttribute("sizes"); img.src = url; };
      const restore = () => {
        img.src = original.src;
        if (original.srcset) img.setAttribute("srcset", original.srcset);
        if (original.sizes) img.setAttribute("sizes", original.sizes);
      };
      swatches.forEach((a) => {
        const pre = new Image(); pre.src = a.dataset.hoverImg;
        a.addEventListener("mouseenter", () => swap(a.dataset.hoverImg));
        a.addEventListener("focus", () => swap(a.dataset.hoverImg));
        a.addEventListener("mouseleave", restore);
        a.addEventListener("blur", restore);
      });
    });
  };

  /* Cart drawer — rendered client-side from the Ajax Cart API */
  AMICE.CartDrawer = function () {
    const drawer = $("[data-cart]");
    const overlay = $("[data-overlay]");
    if (!drawer) return;
    const body = $("[data-cart-body]", drawer);
    const countEls = $$("[data-cart-count]");
    const subtotalEl = $("[data-cart-subtotal]");
    let cart = null;

    const render = () => {
      if (!cart) return;
      countEls.forEach((e) => { e.classList.toggle("is-on", cart.item_count > 0); });
      if (subtotalEl) subtotalEl.innerHTML = money(cart.items_subtotal_price);
      if (!body) return;
      if (!cart.items.length) {
        body.innerHTML = '<p class="muted" style="padding:2rem 0;text-align:center">' + (window.AMICE.t && window.AMICE.t.cart_empty || 'Your bag is empty.') + '</p>';
        return;
      }
      body.innerHTML = cart.items.map((l, i) => `
        <div class="cart-line" data-line-key="${l.key}">
          <img src="${l.image ? l.image.replace(/(\.[a-z]+)(\?|$)/, '_200x$1$2') : ''}" alt="${l.product_title}">
          <div>
            <h4>${l.product_title}</h4><div class="v">${l.variant_title || ''}${l.properties && Object.keys(l.properties).length ? ' · ' + Object.entries(l.properties).map(([k, v]) => k + ' “' + v + '”').join(' · ') : ''}</div>
            <div class="qty" data-i="${i}"><button data-q="-1" aria-label="Decrease">−</button><span>${l.quantity}</span><button data-q="1" aria-label="Increase">+</button></div>
          </div>
          <div style="text-align:right"><div class="cart-line__price">${money(l.final_line_price)}</div><button class="cart-line__rm" data-rm="${i}">Remove</button></div>
        </div>`).join("");
    };

    const open = (v) => { drawer.dataset.open = v ? "true" : "false"; if (overlay) overlay.dataset.open = v ? "true" : "false"; document.body.style.overflow = v ? "hidden" : ""; };
    const refresh = () => CartAPI.getCart().then((c) => { cart = c; render(); });

    body && body.addEventListener("click", (e) => {
      const q = e.target.closest("[data-q]"), rm = e.target.closest("[data-rm]");
      if (q) {
        const i = +q.closest(".qty").dataset.i;
        const line = cart.items[i];
        CartAPI.changeItem(line.key, Math.max(0, line.quantity + +q.dataset.q)).then((c) => { cart = c; render(); });
      } else if (rm) {
        CartAPI.removeItem(cart.items[+rm.dataset.rm].key).then((c) => { cart = c; render(); });
      }
    });

    $$("[data-cart-open]").forEach((b) => b.addEventListener("click", (e) => { e.preventDefault(); refresh().then(() => open(true)); }));
    $$("[data-cart-close]").forEach((b) => b.addEventListener("click", () => open(false)));
    overlay && overlay.addEventListener("click", () => open(false));
    document.addEventListener("keydown", (e) => { if (e.key === "Escape") open(false); });

    Utils.on("cart:added", () => refresh().then(() => open(true)));
    refresh();
  };

  /* Cart drawer — personaliseer een bagagelabel (kleur + initialen + folie) in de wagen.
     Houdt exact dezelfde line item properties aan als de oude flow: Initials, Monogram, For. */
  AMICE.CartTag = function () {
    const root = $("[data-carttag]");
    if (!root || root._bound) return;
    root._bound = true;
    const toggle = $("[data-carttag-toggle]", root);
    const bodyEl = $("[data-carttag-body]", root);
    const input = $("[data-carttag-initials]", root);
    const preview = $("[data-carttag-preview]", root);
    const colourWrap = $("[data-carttag-colours]:not(.drawer__tag-foils)", root);
    const foilWrap = $("[data-carttag-foils]", root);
    const addBtn = $("[data-carttag-add]", root);
    const feeVariant = +root.dataset.feeVariant || 0;

    let variants = [];
    const dataEl = $("[data-carttag-variants]", root);
    if (dataEl) { try { variants = JSON.parse(dataEl.textContent); } catch (e) { /* leave empty */ } }
    if (!variants.length) return;
    let tagVariant = variants[0];

    const SWATCHES = { orange: "#C4622D", oranje: "#C4622D", green: "#154A45", groen: "#154A45", black: "#161614", zwart: "#161614", silver: "#C8C8C4", zilver: "#C8C8C4" };
    if (colourWrap && variants.length > 1) {
      colourWrap.innerHTML = variants.map((v, i) => {
        const key = Object.keys(SWATCHES).find((k) => v.title.toLowerCase().includes(k));
        return `<button type="button" class="carttag__sw" data-tagvar-i="${i}" aria-pressed="${i === 0}" aria-label="${v.title}"><span style="background:${key ? SWATCHES[key] : "#b9b9b4"}"></span>${v.title}</button>`;
      }).join("");
      $$("[data-tagvar-i]", colourWrap).forEach((b) => b.addEventListener("click", () => {
        tagVariant = variants[+b.dataset.tagvarI];
        $$("[data-tagvar-i]", colourWrap).forEach((x) => x.setAttribute("aria-pressed", "false"));
        b.setAttribute("aria-pressed", "true");
      }));
    }

    const foils = (root.dataset.foils || "").split(",").map((f) => {
      const [name, colour] = f.split(":");
      return name && name.trim() ? { name: name.trim(), colour: (colour || "#3a352f").trim() } : null;
    }).filter(Boolean);
    let foil = foils[0] || null;
    if (foilWrap && foils.length) {
      foilWrap.innerHTML = foils.map((f, i) =>
        `<button type="button" class="carttag__sw" data-foil-i="${i}" aria-pressed="${i === 0}" aria-label="${f.name}"><span style="background:${f.colour}"></span>${f.name}</button>`
      ).join("");
      $$("[data-foil-i]", foilWrap).forEach((b) => b.addEventListener("click", () => {
        foil = foils[+b.dataset.foilI];
        $$("[data-foil-i]", foilWrap).forEach((x) => x.setAttribute("aria-pressed", "false"));
        b.setAttribute("aria-pressed", "true");
      }));
    }

    const updatePreview = () => { const v = ((input && input.value) || "").toUpperCase().trim(); if (preview) preview.textContent = v || "•••"; };
    toggle && toggle.addEventListener("click", () => {
      const on = toggle.dataset.on === "true";
      toggle.dataset.on = on ? "false" : "true";
      if (bodyEl) bodyEl.hidden = on;
    });
    input && input.addEventListener("input", () => { input.value = input.value.replace(/[^a-zA-Z]/g, "").toUpperCase(); updatePreview(); });

    addBtn && addBtn.addEventListener("click", () => {
      if (!tagVariant || !tagVariant.id) return;
      const ini = ((input && input.value) || "").toUpperCase().trim();
      const props = {};
      if (ini) { props.Initials = ini; if (foil) props.Monogram = foil.name; }
      addBtn.disabled = true;
      let chain = CartAPI.addItem(tagVariant.id, 1, Object.keys(props).length ? props : undefined);
      if (ini && feeVariant && feeVariant !== tagVariant.id) {
        const feeProps = { Initials: ini };
        if (foil) feeProps.Monogram = foil.name;
        feeProps.For = root.dataset.tagTitle || "";
        chain = chain.then(() => CartAPI.addItem(feeVariant, 1, feeProps));
      }
      chain.then(() => {
        Utils.emit("cart:added");
        if (input) input.value = ""; updatePreview();
        if (bodyEl) bodyEl.hidden = true;
        if (toggle) toggle.dataset.on = "false";
        addBtn.disabled = false;
      }).catch(() => { addBtn.disabled = false; });
    });
  };

  /* Product: gallery, options → variant resolution, add to cart, personalise hand-off */
  AMICE.Product = function () {
    const pdp = $("[data-pdp]");
    if (!pdp) return;

    let variants = [];
    const dataEl = $("[data-product-variants]", pdp);
    if (dataEl) { try { variants = JSON.parse(dataEl.textContent); } catch (e) { /* leave empty */ } }

    const main = $("[data-gallery-main]", pdp);
    const setMain = (url) => {
      if (!main || !url) return;
      main.removeAttribute("srcset");
      main.removeAttribute("sizes");
      main.src = url;
    };
    $$("[data-thumb]", pdp).forEach((btn) => btn.addEventListener("click", () => {
      $$("[data-thumb]", pdp).forEach((b) => b.setAttribute("aria-current", "false"));
      btn.setAttribute("aria-current", "true");
      setMain(btn.dataset.full || $("img", btn).src);
    }));

    const state = {};
    const groups = $$("[data-opt-group]", pdp);
    const optionOrder = groups.map((g) => g.dataset.optGroup);

    const currentVariant = () => {
      if (!variants.length) return null;
      return variants.find((v) => v.options.every((val, idx) => {
        const key = optionOrder[idx];
        return !state[key] || state[key] === val;
      })) || null;
    };

    const priceEls = $$("[data-product-price]", pdp);
    const addBtn = $("[data-add]", pdp);

    const colourKey = optionOrder.find((k) => /^(colour|color|kleur)$/i.test(k));
    const scopedEls = $$("[data-thumb][data-scope], .gallery__slide[data-scope]", pdp)
      .filter((el) => el.dataset.scope);
    const filterGallery = () => {
      if (!colourKey || !scopedEls.length) return;
      const cur = (state[colourKey] || "").toLowerCase();
      scopedEls.forEach((el) => {
        el.style.display = !cur || el.dataset.scope === cur ? "" : "none";
      });
      const current = $("[data-thumb][aria-current='true']", pdp);
      if (current && current.style.display === "none") {
        const next = $$("[data-thumb]", pdp).find((b) => b.style.display !== "none");
        if (next) {
          $$("[data-thumb]", pdp).forEach((b) => b.setAttribute("aria-current", "false"));
          next.setAttribute("aria-current", "true");
          setMain(next.dataset.full || ($("img", next) || {}).src);
        }
      }
    };

    const syncUI = () => {
      const v = currentVariant();
      if (!v) return;
      priceEls.forEach((el) => { el.innerHTML = money(v.price); });
      if (addBtn) {
        const label = addBtn.dataset.addLabel || 'Add to bag';
        addBtn.disabled = !v.available;
        addBtn.textContent = v.available ? (label + ' — ') : (addBtn.dataset.soldoutLabel || 'Sold out');
        if (v.available) addBtn.insertAdjacentHTML('beforeend', money(v.price));
      }
      if (v.featured_image) setMain(v.featured_image);
      filterGallery();
    };

    groups.forEach((group) => {
      const key = group.dataset.optGroup;
      const label = $("[data-opt-value='" + key + "']");
      const btns = $$("[data-opt]", group);
      const select = (btn) => {
        btns.forEach((b) => b.setAttribute("aria-pressed", "false"));
        btn.setAttribute("aria-pressed", "true");
        state[key] = btn.dataset.opt;
        if (label) label.textContent = btn.dataset.note ? btn.dataset.opt + " — " + btn.dataset.note : btn.dataset.opt;
        if (btn.dataset.img) setMain(btn.dataset.img);
        if (!currentVariant() && variants.length) {
          const idx = optionOrder.indexOf(key);
          const alt = variants.find((v) => v.options[idx] === state[key] && v.available)
            || variants.find((v) => v.options[idx] === state[key]);
          if (alt) {
            alt.options.forEach((val, i) => { state[optionOrder[i]] = val; });
            groups.forEach((g) => {
              const k = g.dataset.optGroup;
              const gBtns = $$("[data-opt]", g);
              gBtns.forEach((b) => b.setAttribute("aria-pressed", String(b.dataset.opt === state[k])));
              const pressed = gBtns.find((b) => b.dataset.opt === state[k]);
              const gLabel = $("[data-opt-value='" + k + "']");
              if (gLabel && pressed) gLabel.textContent = pressed.dataset.note ? pressed.dataset.opt + " — " + pressed.dataset.note : pressed.dataset.opt;
            });
          }
        }
        syncUI();
      };
      btns.forEach((b) => b.addEventListener("click", () => select(b)));
      select(btns.find((b) => b.getAttribute("aria-pressed") === "true") || btns[0]);
    });

    const addonWrap = $("[data-addons]");
    if (addonWrap) {
      $$("[data-addon]", addonWrap).forEach((tile) => {
        const check = $("[data-addon-check]", tile);
        const sync = () => { tile.dataset.on = check && check.checked ? "true" : "false"; };
        check && check.addEventListener("change", sync);
        $$("[data-addon-variant]", tile).forEach((b) => b.addEventListener("click", (e) => {
          e.preventDefault();
          $$("[data-addon-variant]", tile).forEach((x) => x.setAttribute("aria-pressed", "false"));
          b.setAttribute("aria-pressed", "true");
          tile.dataset.variant = b.dataset.addonVariant;
          const img = $("img", tile);
          if (img && b.dataset.img) { img.removeAttribute("srcset"); img.removeAttribute("sizes"); img.src = b.dataset.img; }
          if (check && !check.checked) { check.checked = true; }
          sync();
        }));
        sync();
      });
    }
    AMICE.selectedAddons = () => addonWrap
      ? $$("[data-addon]", addonWrap).filter((t) => t.dataset.on === "true" && +t.dataset.variant).map((t) => +t.dataset.variant)
      : [];

    addBtn && addBtn.addEventListener("click", () => {
      const v = currentVariant();
      const variantId = v ? v.id : +pdp.dataset.variantId;
      if (!variantId) return;
      const extras = AMICE.selectedAddons();
      if (AMICE._openPersonalise) AMICE._openPersonalise(variantId, extras);
      else {
        let chain = CartAPI.addItem(variantId, 1);
        extras.forEach((id) => { chain = chain.then(() => CartAPI.addItem(id, 1)); });
        chain.then(() => Utils.emit("cart:added"));
      }
    });

  };

  /* Personalise step — tag colour + initials + initials colour as line item properties */
  AMICE.Personalise = function () {
    const modal = $("[data-pmodal]");
    if (!modal) return;
    const opt = $("[data-pmodal-toggle]", modal);
    const engrave = $("[data-pmodal-engrave]", modal);
    const input = $("[data-pmodal-initials]", modal);
    const preview = $("[data-pmodal-preview]", modal);
    const imgEl = $("[data-pmodal-img]", modal);
    let pendingVariant = null, pendingExtras = [], tagOn = false;
    const feeVariant = +modal.dataset.feeVariant || 0;

    let tagVariants = [];
    const dataEl = $("[data-pmodal-variants]", modal);
    if (dataEl) { try { tagVariants = JSON.parse(dataEl.textContent); } catch (e) { /* leave empty */ } }
    let tagVariant = tagVariants[0] || { id: +modal.dataset.tagVariant || 0 };

    const SWATCHES = { orange: "#C4622D", oranje: "#C4622D", green: "#154A45", groen: "#154A45", black: "#161614", zwart: "#161614" };
    const colourWrap = $("[data-pmodal-colours]", modal);
    if (colourWrap && tagVariants.length > 1) {
      colourWrap.innerHTML = tagVariants.map((v, i) => {
        const key = Object.keys(SWATCHES).find((k) => v.title.toLowerCase().includes(k));
        return `<button type="button" class="pmodal__sw" data-tagvar-i="${i}" aria-pressed="${i === 0}" aria-label="${v.title}"><span style="background:${key ? SWATCHES[key] : "#b9b9b4"}"></span>${v.title}</button>`;
      }).join("");
      $$("[data-tagvar-i]", colourWrap).forEach((b) => b.addEventListener("click", () => {
        tagVariant = tagVariants[+b.dataset.tagvarI];
        $$("[data-tagvar-i]", colourWrap).forEach((x) => x.setAttribute("aria-pressed", "false"));
        b.setAttribute("aria-pressed", "true");
        if (imgEl && tagVariant.image) { imgEl.removeAttribute("srcset"); imgEl.removeAttribute("sizes"); imgEl.src = tagVariant.image; }
      }));
    }

    const foils = (modal.dataset.foils || "").split(",").map((f) => {
      const [name, colour] = f.split(":");
      return name && name.trim() ? { name: name.trim(), colour: (colour || "#3a352f").trim() } : null;
    }).filter(Boolean);
    let foil = foils[0] || null;
    const foilWrap = $("[data-pmodal-foils]", modal);
    if (foilWrap && foils.length) {
      foilWrap.innerHTML = foils.map((f, i) =>
        `<button type="button" class="pmodal__sw" data-foil-i="${i}" aria-pressed="${i === 0}" aria-label="${f.name}"><span style="background:${f.colour}"></span>${f.name}</button>`
      ).join("");
      $$("[data-foil-i]", foilWrap).forEach((b) => b.addEventListener("click", () => {
        foil = foils[+b.dataset.foilI];
        $$("[data-foil-i]", foilWrap).forEach((x) => x.setAttribute("aria-pressed", "false"));
        b.setAttribute("aria-pressed", "true");
      }));
    }

    const setTag = (v) => { tagOn = v; if (opt) opt.dataset.on = v ? "true" : "false"; if (engrave) engrave.dataset.show = v ? "true" : "false"; };
    const handleOpt = $("[data-pmodal-handle]", modal);
    let handleOn = false;
    const setHandle = (v) => { handleOn = v; if (handleOpt) handleOpt.dataset.on = v ? "true" : "false"; };
    handleOpt && handleOpt.addEventListener("click", () => setHandle(!handleOn));
    const updatePreview = () => { const v = ((input && input.value) || "").toUpperCase().trim(); if (preview) preview.textContent = v || "•••"; };
    const close = () => { modal.dataset.open = "false"; modal.setAttribute("aria-hidden", "true"); document.body.style.overflow = ""; };

    AMICE._openPersonalise = (variantId, extras) => {
      pendingVariant = variantId; pendingExtras = extras || []; setTag(false); setHandle(false); if (input) input.value = ""; updatePreview();
      modal.dataset.open = "true"; modal.setAttribute("aria-hidden", "false"); document.body.style.overflow = "hidden";
    };

    opt && opt.addEventListener("click", () => setTag(!tagOn));
    input && input.addEventListener("input", () => { input.value = input.value.replace(/[^a-zA-Z]/g, "").toUpperCase(); updatePreview(); });

    const finish = (withTag, withHandle) => {
      const variantId = pendingVariant; pendingVariant = null;
      const extras = pendingExtras; pendingExtras = [];
      close();
      if (!variantId) return;
      let chain = CartAPI.addItem(variantId, 1);
      if (withTag && tagVariant.id) {
        const ini = ((input && input.value) || "").toUpperCase().trim();
        const props = {};
        if (ini) { props.Initials = ini; if (foil) props.Monogram = foil.name; }
        chain = chain.then(() => CartAPI.addItem(tagVariant.id, 1, Object.keys(props).length ? props : undefined));
        if (ini && feeVariant && feeVariant !== tagVariant.id) {
          const feeProps = { Initials: ini };
          if (foil) feeProps.Monogram = foil.name;
          feeProps.For = tagVariant.product_title || (modal.dataset.tagTitle || "");
          chain = chain.then(() => CartAPI.addItem(feeVariant, 1, feeProps));
        }
      }
      const handleVariantId = withHandle && handleOpt ? +handleOpt.dataset.handleVariant : 0;
      if (handleVariantId) chain = chain.then(() => CartAPI.addItem(handleVariantId, 1));
      extras.forEach((id) => { chain = chain.then(() => CartAPI.addItem(id, 1)); });
      chain.then(() => Utils.emit("cart:added"));
    };
    $("[data-pmodal-confirm]", modal) && $("[data-pmodal-confirm]", modal).addEventListener("click", () => finish(tagOn, handleOn));
    $("[data-pmodal-skip]", modal) && $("[data-pmodal-skip]", modal).addEventListener("click", () => finish(false, false));
    $$("[data-pmodal-close]", modal).forEach((b) => b.addEventListener("click", close));
    document.addEventListener("keydown", (e) => { if (e.key === "Escape" && modal.dataset.open === "true") close(); });
  };

  /* Monogram configurator */
  AMICE.Configurator = function () {
    const root = $("[data-config]");
    if (!root) return;

    let products = [];
    try { products = JSON.parse($("[data-config-products]", root).textContent); } catch (e) { return; }
    if (!products.length) return;

    const foils = (root.dataset.foils || "Blind:#3a352f").split(",").map((f) => {
      const [name, colour] = f.split(":");
      return { name: name.trim(), colour: (colour || "#3a352f").trim() };
    });

    const state = { product: products[0], variant: products[0].variants[0], initials: "", foil: foils[0], step: 0 };
    const steps = ["product", "colour", "initials", "review"];
    const fee = { id: +root.dataset.feeVariant || 0, price: +root.dataset.feePrice || 0, label: root.dataset.feeLabel || "Engraving" };
    const feeApplies = () => !!(fee.id && state.initials);

    const img = $("[data-config-img]", root);
    const mono = $("[data-config-mono]", root);
    const stepTitle = $("[data-config-steptitle]", root);
    const dots = $$("[data-config-dot]", root);
    const prev = $("[data-config-prev]", root);
    const next = $("[data-config-next]", root);
    const priceEl = $("[data-config-price]", root);
    const summaryEl = $("[data-config-summary]", root);
    const addBtn = $("[data-config-add]", root);
    const input = $("[data-config-initials]", root);
    const titles = { product: root.dataset.tProduct, colour: root.dataset.tColour, initials: root.dataset.tInitials, review: root.dataset.tReview };

    const swapImage = (src) => {
      if (!img || img.src === src) return;
      img.classList.add("is-swapping");
      const pre = new Image();
      pre.onload = () => { img.removeAttribute("srcset"); img.removeAttribute("sizes"); img.src = src; requestAnimationFrame(() => img.classList.remove("is-swapping")); };
      pre.onerror = () => img.classList.remove("is-swapping");
      pre.src = src;
    };

    const renderPreview = () => {
      const p = state.product;
      if (img) {
        swapImage(state.variant.image || p.image);
        img.alt = p.title;
      }
      if (mono) {
        const txt = state.initials || "A·M";
        if (mono.textContent !== txt) {
          mono.classList.remove("is-in");
          void mono.offsetWidth;
          mono.textContent = txt;
        }
        mono.classList.add("is-in");
        mono.style.opacity = state.initials ? "" : 0.35;
        mono.style.setProperty("--mx", p.pos.x + "%");
        mono.style.setProperty("--my", p.pos.y + "%");
        mono.style.setProperty("--mr", p.pos.r + "deg");
        mono.style.setProperty("--ms", p.pos.s + "rem");
        mono.style.setProperty("--mc", state.foil.colour);
      }
      if (priceEl) priceEl.innerHTML = Utils.formatMoney(state.variant.price + (feeApplies() ? fee.price : 0), window.AMICE.moneyFormat);
      const feeNote = $("[data-config-fee]", root);
      if (feeNote) feeNote.hidden = !feeApplies();
      if (summaryEl) summaryEl.textContent = [p.title, state.variant.title !== "Default Title" ? state.variant.title : "", state.initials ? ("“" + state.initials + "” · " + state.foil.name) : "", feeApplies() ? (fee.label + " +" + Utils.formatMoney(fee.price, window.AMICE.moneyFormat).replace(/<[^>]+>/g, "")) : ""].filter(Boolean).join(" · ");
    };

    const renderStep = () => {
      $$("[data-config-step]", root).forEach((el) => { el.dataset.active = el.dataset.configStep === steps[state.step] ? "true" : "false"; });
      dots.forEach((d, i) => { d.dataset.active = i === state.step ? "true" : "false"; d.dataset.done = i < state.step ? "true" : "false"; });
      if (stepTitle) stepTitle.textContent = titles[steps[state.step]] || "";
      if (prev) prev.disabled = state.step === 0;
      if (next) next.disabled = state.step === steps.length - 1;
      if (addBtn) addBtn.style.display = state.step === steps.length - 1 ? "" : "none";
    };

    const renderColourOptions = () => {
      const wrap = $("[data-config-colours]", root);
      if (!wrap) return;
      wrap.innerHTML = state.product.variants.map((v, i) => `
        <button type="button" class="config__opt" data-variant-i="${i}" data-on="${v.id === state.variant.id}">
          <span class="swatch-lg" style="background:${v.swatch || '#b9b9b4'}"></span>
          <b>${v.title}</b>
        </button>`).join("");
      $$("[data-variant-i]", wrap).forEach((b) => b.addEventListener("click", () => {
        state.variant = state.product.variants[+b.dataset.variantI];
        $$("[data-variant-i]", wrap).forEach((x) => { x.dataset.on = "false"; });
        b.dataset.on = "true";
        renderPreview();
      }));
    };

    const prodWrap = $("[data-config-productlist]", root);
    if (prodWrap) {
      prodWrap.innerHTML = products.map((p, i) => `
        <button type="button" class="config__opt" data-product-i="${i}" data-on="${i === 0}">
          <figure><img src="${p.variants[0].image || p.image}" alt="${p.title}" loading="lazy"></figure>
          <b>${p.title}</b>
          <span>${Utils.formatMoney(p.variants[0].price, window.AMICE.moneyFormat)}</span>
        </button>`).join("");
      $$("[data-product-i]", prodWrap).forEach((b) => b.addEventListener("click", () => {
        state.product = products[+b.dataset.productI];
        state.variant = state.product.variants[0];
        $$("[data-product-i]", prodWrap).forEach((x) => { x.dataset.on = "false"; });
        b.dataset.on = "true";
        renderColourOptions(); renderPreview();
      }));
    }

    const foilWrap = $("[data-config-foils]", root);
    if (foilWrap) {
      foilWrap.innerHTML = foils.map((f, i) => `
        <button type="button" class="config__opt" data-foil-i="${i}" data-on="${i === 0}">
          <span class="swatch-lg" style="background:${f.colour}"></span>
          <b>${f.name}</b>
        </button>`).join("");
      $$("[data-foil-i]", foilWrap).forEach((b) => b.addEventListener("click", () => {
        state.foil = foils[+b.dataset.foilI];
        $$("[data-foil-i]", foilWrap).forEach((x) => { x.dataset.on = "false"; });
        b.dataset.on = "true";
        renderPreview();
      }));
    }

    input && input.addEventListener("input", () => {
      input.value = input.value.replace(/[^a-zA-Z]/g, "").toUpperCase();
      state.initials = input.value;
      renderPreview();
    });

    prev && prev.addEventListener("click", () => { if (state.step > 0) { state.step--; renderStep(); } });
    next && next.addEventListener("click", () => { if (state.step < steps.length - 1) { state.step++; renderStep(); } });
    dots.forEach((d, i) => d.addEventListener("click", () => { state.step = i; renderStep(); }));
    document.addEventListener("keydown", (e) => {
      if (document.activeElement && /INPUT|TEXTAREA/.test(document.activeElement.tagName)) return;
      if (e.key === "ArrowRight" && state.step < steps.length - 1) { state.step++; renderStep(); }
      if (e.key === "ArrowLeft" && state.step > 0) { state.step--; renderStep(); }
    });

    addBtn && addBtn.addEventListener("click", () => {
      const props = {};
      if (state.initials) { props.Initials = state.initials; props.Monogram = state.foil.name; }
      let chain = CartAPI.addItem(state.variant.id, 1, Object.keys(props).length ? props : undefined);
      if (feeApplies()) {
        const feeProps = { Initials: state.initials, Monogram: state.foil.name, For: state.product.title };
        chain = chain.then(() => CartAPI.addItem(fee.id, 1, feeProps));
      }
      chain.then(() => Utils.emit("cart:added"));
    });

    renderColourOptions(); renderPreview(); renderStep();
  };

  /* Bundle & save */
  AMICE.Bundle = function () {
    const btn = $("[data-add-bundle]");
    if (!btn) return;
    const ids = (btn.dataset.variantIds || "").split(",").map((s) => parseInt(s.trim(), 10)).filter(Boolean);
    if (!ids.length) return;
    btn.addEventListener("click", () => {
      ids.reduce((chain, id) => chain.then(() => CartAPI.addItem(id, 1)), Promise.resolve())
        .then(() => Utils.emit("cart:added"));
    });
  };

  /* Accordions */
  AMICE.Accordion = function () {
    $$("[data-acc-btn]").forEach((btn) => {
      const panel = btn.parentElement.querySelector("[data-acc-panel]");
      if (!panel) return;
      if (btn.getAttribute("aria-expanded") === "true") panel.style.height = "auto";
      btn.addEventListener("click", () => {
        const open = btn.getAttribute("aria-expanded") === "true";
        btn.setAttribute("aria-expanded", open ? "false" : "true");
        panel.style.height = open ? "0px" : panel.scrollHeight + "px";
      });
    });
  };

  /* Reviews carousel */
  AMICE.Reviews = function () {
    const track = $("[data-reviews]");
    if (!track) return;
    const step = () => Math.min(track.clientWidth * 0.85, 400);
    $("[data-rev-prev]") && $("[data-rev-prev]").addEventListener("click", () => track.scrollBy({ left: -step(), behavior: "smooth" }));
    $("[data-rev-next]") && $("[data-rev-next]").addEventListener("click", () => track.scrollBy({ left: step(), behavior: "smooth" }));
    let down = false, x0 = 0, s0 = 0;
    track.addEventListener("pointerdown", (e) => { down = true; x0 = e.pageX; s0 = track.scrollLeft; track.classList.add("is-drag"); });
    window.addEventListener("pointerup", () => { down = false; track.classList.remove("is-drag"); });
    window.addEventListener("pointermove", (e) => { if (down) track.scrollLeft = s0 - (e.pageX - x0); });
  };

  /* Scroll reveal */
  AMICE.Reveal = function () {
    const els = $$(".reveal");
    if (!els.length || !("IntersectionObserver" in window)) { els.forEach((e) => e.classList.add("in")); return; }
    const reveal = (el) => {
      const imgs = $$("img", el).filter((img) => !img.complete);
      if (!imgs.length) { el.classList.add("in"); return; }
      imgs.forEach((img) => { img.loading = "eager"; img.fetchPriority = "high"; });
      let done = false;
      const go = () => {
        if (done) return;
        done = true;
        imgs.filter((img) => !img.complete).forEach((img) => {
          img.classList.add("img-fade");
          const show = () => img.classList.add("img-in");
          img.addEventListener("load", show, { once: true });
          img.addEventListener("error", show, { once: true });
        });
        el.classList.add("in");
      };
      Promise.all(imgs.map((img) => new Promise((res) => {
        img.addEventListener("load", res, { once: true });
        img.addEventListener("error", res, { once: true });
      }))).then(go);
      setTimeout(go, 1400);
    };
    const io = new IntersectionObserver((ents) => ents.forEach((en) => { if (en.isIntersecting) { reveal(en.target); io.unobserve(en.target); } }), { threshold: 0.12, rootMargin: "0px 0px -8% 0px" });
    const warm = new IntersectionObserver((ents) => ents.forEach((en) => {
      if (!en.isIntersecting) return;
      $$('img[loading="lazy"]', en.target).forEach((img) => { img.loading = "eager"; });
      warm.unobserve(en.target);
    }), { rootMargin: "1000px 0px" });
    els.forEach((e) => { io.observe(e); warm.observe(e); });
  };

  /* Newsletter */
  AMICE.Newsletter = function () {
    $$("[data-newsletter]").forEach((form) => {
      if (form.getAttribute("action")) return;
      form.addEventListener("submit", (e) => {
        e.preventDefault(); form.innerHTML = '<p class="lede">Welcome aboard. Your €30 code is on its way.</p>';
      });
    });
  };

  /* Interactive lock guide */
  AMICE.LockGuide = function () {
    const root = $("[data-lockguide]");
    if (!root) return;
    let steps = [];
    const stepsEl = $("[data-lg-steps]", root) || $("[data-lg-steps]");
    try { steps = JSON.parse(stepsEl.textContent); } catch (e) { return; }
    if (!steps.length) return;
    const numEl = $("[data-lg-num]", root), totEl = $("[data-lg-tot]", root);
    const descEl = $("[data-lg-desc]", root), imgEl = $("[data-lg-img]", root);
    const bar = $("[data-lg-bar]", root), prev = $("[data-lg-prev]", root), next = $("[data-lg-next]", root);
    let i = 0;
    bar.innerHTML = steps.map((_, n) => `<button class="lg__seg" data-seg="${n}" aria-label="Step ${n + 1}"></button>`).join("");
    const segs = $$("[data-seg]", bar);
    if (totEl) totEl.textContent = String(steps.length).padStart(2, "0");
    const render = () => {
      const s = steps[i];
      if (numEl) numEl.textContent = String(i + 1).padStart(2, "0");
      imgEl.style.opacity = 0;
      setTimeout(() => { imgEl.src = s.img; imgEl.alt = s.t || "Lock step"; imgEl.style.opacity = 1; }, 180);
      descEl.innerHTML = (s.t ? `<b>${s.t}.</b> ` : "") + s.d;
      segs.forEach((seg, n) => seg.setAttribute("data-done", n <= i ? "true" : "false"));
      prev.disabled = i === 0; next.disabled = i === steps.length - 1;
    };
    prev.addEventListener("click", () => { if (i > 0) { i--; render(); } });
    next.addEventListener("click", () => { if (i < steps.length - 1) { i++; render(); } });
    segs.forEach((seg) => seg.addEventListener("click", () => { i = +seg.dataset.seg; render(); }));
    root.addEventListener("keydown", (e) => { if (e.key === "ArrowLeft") prev.click(); if (e.key === "ArrowRight") next.click(); });
    render();
  };

  /* Entry loader */
  AMICE.Loader = function () {
    const el = $("[data-loader]");
    if (!el) return;
    const done = () => { el.classList.add("is--done"); document.body.style.overflow = ""; setTimeout(() => el.remove(), 1000); };
    const reduced = matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced) { el.remove(); return; }
    document.body.style.overflow = "hidden";
    const start = Date.now();
    const finish = () => setTimeout(done, Math.max(0, 1650 - (Date.now() - start)));
    if (document.readyState === "complete") finish();
    else window.addEventListener("load", finish);
    setTimeout(done, 3200);
  };

  /* Announcement bar */
  AMICE.Announce = function () {
    const track = $(".announce__track");
    if (!track) return;
    const spans = $$("span", track);
    if (!spans.length) return;
    spans.forEach((s, n) => s.classList.toggle("is-active", n === 0));
    if (spans.length < 2 || matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const interval = (+track.dataset.interval || 5) * 1000;
    let i = 0;
    setInterval(() => {
      spans[i].classList.remove("is-active");
      i = (i + 1) % spans.length;
      spans[i].classList.add("is-active");
    }, interval);
  };

  /* Local time + weather */
  AMICE.LocalWx = function () {
    const els = $$("[data-localwx]");
    if (!els.length) return;

    const CAPITALS = {
      NL: { c: "Amsterdam", lat: 52.37, lon: 4.90, tz: "Europe/Amsterdam" },
      BE: { c: "Brussels", lat: 50.85, lon: 4.35, tz: "Europe/Brussels" },
      DE: { c: "Berlin", lat: 52.52, lon: 13.40, tz: "Europe/Berlin" },
      FR: { c: "Paris", lat: 48.85, lon: 2.35, tz: "Europe/Paris" },
      GB: { c: "London", lat: 51.51, lon: -0.13, tz: "Europe/London" },
      IE: { c: "Dublin", lat: 53.35, lon: -6.26, tz: "Europe/Dublin" },
      ES: { c: "Madrid", lat: 40.42, lon: -3.70, tz: "Europe/Madrid" },
      PT: { c: "Lisbon", lat: 38.72, lon: -9.14, tz: "Europe/Lisbon" },
      IT: { c: "Rome", lat: 41.90, lon: 12.50, tz: "Europe/Rome" },
      AT: { c: "Vienna", lat: 48.21, lon: 16.37, tz: "Europe/Vienna" },
      CH: { c: "Bern", lat: 46.95, lon: 7.45, tz: "Europe/Zurich" },
      DK: { c: "Copenhagen", lat: 55.68, lon: 12.57, tz: "Europe/Copenhagen" },
      SE: { c: "Stockholm", lat: 59.33, lon: 18.06, tz: "Europe/Stockholm" },
      NO: { c: "Oslo", lat: 59.91, lon: 10.75, tz: "Europe/Oslo" },
      FI: { c: "Helsinki", lat: 60.17, lon: 24.94, tz: "Europe/Helsinki" },
      PL: { c: "Warsaw", lat: 52.23, lon: 21.01, tz: "Europe/Warsaw" },
      CZ: { c: "Prague", lat: 50.08, lon: 14.44, tz: "Europe/Prague" },
      LU: { c: "Luxembourg", lat: 49.61, lon: 6.13, tz: "Europe/Luxembourg" },
      GR: { c: "Athens", lat: 37.98, lon: 23.73, tz: "Europe/Athens" },
      US: { c: "Washington", lat: 38.90, lon: -77.04, tz: "America/New_York" },
      CA: { c: "Ottawa", lat: 45.42, lon: -75.70, tz: "America/Toronto" },
      AU: { c: "Canberra", lat: -35.28, lon: 149.13, tz: "Australia/Sydney" },
    };
    const DEFAULT = CAPITALS.NL;
    const codes = { 0: "Clear", 1: "Mainly clear", 2: "Partly cloudy", 3: "Overcast", 45: "Fog", 48: "Fog", 51: "Drizzle", 53: "Drizzle", 55: "Drizzle", 61: "Rain", 63: "Rain", 65: "Heavy rain", 71: "Snow", 73: "Snow", 75: "Snow", 80: "Showers", 81: "Showers", 82: "Showers", 95: "Storm", 96: "Storm", 99: "Storm" };

    const render = (cap) => {
      const tick = () => {
        let t = "--:--";
        try { t = new Intl.DateTimeFormat("en-GB", { hour: "2-digit", minute: "2-digit", timeZone: cap.tz }).format(new Date()); } catch (e) {}
        els.forEach((el) => {
          const n = $("[data-localwx-time]", el); if (n) n.textContent = t;
          const ci = $("[data-localwx-city]", el); if (ci) ci.textContent = cap.c;
        });
      };
      tick();
      if (AMICE._wxTimer) clearInterval(AMICE._wxTimer);
      AMICE._wxTimer = setInterval(tick, 30000);

      fetch("https://api.open-meteo.com/v1/forecast?latitude=" + cap.lat + "&longitude=" + cap.lon + "&current=temperature_2m,weather_code&timezone=auto")
        .then((r) => r.json())
        .then((d) => {
          const c = d && d.current; if (!c) return;
          const temp = Math.round(c.temperature_2m);
          const label = codes[c.weather_code] || "";
          els.forEach((el) => { const n = $("[data-localwx-temp]", el); if (n) n.textContent = temp + "°C" + (label ? " · " + label : ""); });
        })
        .catch(() => { els.forEach((el) => { const n = $("[data-localwx-temp]", el); if (n) n.textContent = cap.c; }); });
    };

    /* Country comes from Shopify (the visitor's market), so no external IP lookup is needed */
    const code = (els[0].dataset.country || (window.Shopify && window.Shopify.country) || "").toUpperCase();
    render(CAPITALS[code] || DEFAULT);
  };

  /* Product delivery date */
  AMICE.DeliveryDate = function () {
    const els = $$("[data-delivery-date]");
    if (!els.length) return;
    const now = new Date();
    const cutoff = +((els[0].dataset.cutoffHour) || 22);
    const extra = now.getHours() >= cutoff ? 1 : 0;
    const d = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    d.setDate(d.getDate() + 2 + extra);
    /* No delivery on Sundays, New Year's Day or Christmas: move to the next delivery day (Saturday is fine) */
    const closed = ["1-1", "12-25", "12-26"];
    while (d.getDay() === 0 || closed.indexOf((d.getMonth() + 1) + "-" + d.getDate()) !== -1) d.setDate(d.getDate() + 1);
    const locale = document.documentElement.lang || "en-GB";
    const txt = d.toLocaleDateString(locale, { weekday: "long", day: "numeric", month: "long" });
    els.forEach((el) => { el.textContent = txt; });
  };

  /* Gallery lightbox */
  AMICE.Lightbox = function () {
    const box = $("[data-lightbox]");
    const opener = $("[data-lightbox-open]");
    if (!box || !opener || box._bound) return;
    box._bound = true;
    let images = [];
    const dataEl = $("[data-lightbox-images]", box);
    if (dataEl) { try { images = JSON.parse(dataEl.textContent); } catch (e) { /* leave empty */ } }
    if (!images.length) return;
    const img = $("[data-lightbox-img]", box);
    const count = $("[data-lightbox-count]", box);
    const closeBtn = $(".lightbox__close", box);
    let i = 0, lastFocus = null;
    const show = (n) => {
      i = (n + images.length) % images.length;
      img.src = images[i];
      if (count) count.textContent = (i + 1) + " / " + images.length;
    };
    const open = () => {
      lastFocus = document.activeElement;
      const mainSrc = ($("[data-gallery-main]") || {}).src || "";
      const idx = images.findIndex((u) => u.split("?")[0] === mainSrc.split("?")[0]);
      show(idx >= 0 ? idx : 0);
      box.dataset.open = "true";
      box.setAttribute("aria-hidden", "false");
      document.body.style.overflow = "hidden";
      if (closeBtn) closeBtn.focus();
    };
    const close = () => {
      box.dataset.open = "false";
      box.setAttribute("aria-hidden", "true");
      document.body.style.overflow = "";
      if (lastFocus) lastFocus.focus();
    };
    opener.addEventListener("click", open);
    $$("[data-lightbox-close]", box).forEach((el) => el.addEventListener("click", close));
    const prev = $("[data-lightbox-prev]", box), next = $("[data-lightbox-next]", box);
    prev && prev.addEventListener("click", () => show(i - 1));
    next && next.addEventListener("click", () => show(i + 1));
    document.addEventListener("keydown", (e) => {
      if (box.dataset.open !== "true") return;
      if (e.key === "Escape") close();
      if (e.key === "ArrowLeft") show(i - 1);
      if (e.key === "ArrowRight") show(i + 1);
    });
  };

  /* Scroll parallax */
  AMICE.Parallax = function () {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const medias = $$(".band__media img, .showroom__media img");
    if (!medias.length || !("IntersectionObserver" in window)) return;
    AMICE._plxActive = [];
    const io = new IntersectionObserver((ents) => {
      ents.forEach((en) => {
        const i = AMICE._plxActive.indexOf(en.target);
        if (en.isIntersecting && i < 0) AMICE._plxActive.push(en.target);
        if (!en.isIntersecting && i >= 0) AMICE._plxActive.splice(i, 1);
      });
    }, { rootMargin: "12% 0px" });
    medias.forEach((img) => { img.classList.add("plx"); io.observe(img); });
    const update = () => {
      AMICE._plxTick = false;
      const vh = window.innerHeight;
      AMICE._plxActive.forEach((img) => {
        const r = img.parentElement.getBoundingClientRect();
        const p = (r.top + r.height / 2 - vh / 2) / (vh / 2 + r.height / 2);
        img.style.transform = "translateY(" + (p * -5).toFixed(2) + "%) scale(1.12)";
      });
    };
    if (!AMICE._plxBound) {
      AMICE._plxBound = true;
      window.addEventListener("scroll", () => {
        if (!AMICE._plxTick) { AMICE._plxTick = true; requestAnimationFrame(AMICE._plxUpdate); }
      }, { passive: true });
    }
    AMICE._plxUpdate = update;
    update();
  };

  /* Video bands */
  AMICE.VideoBand = function () {
    const vids = $$("[data-vband-video]");
    if (!vids.length) return;
    const reduced = matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced) { vids.forEach((v) => { v.removeAttribute("autoplay"); v.pause(); }); return; }
    if (!("IntersectionObserver" in window)) return;
    const io = new IntersectionObserver((ents) => ents.forEach((en) => {
      const v = en.target;
      if (en.isIntersecting) { const p = v.play(); if (p) p.catch(() => {}); }
      else v.pause();
    }), { rootMargin: "10% 0px" });
    vids.forEach((v) => { v.muted = true; io.observe(v); });
  };

  /* Collection grid filter */
  AMICE.FormatFilter = function () {
    const grid = $("[data-product-grid]");
    if (!grid) return;
    const tiles = $$("[data-format-tile]");
    const MAP = {
      "carry-on": ["cabin", "carry"],
      "front-pocket": ["front"],
      "check-in": ["check"],
      "sets": ["set"],
    };
    const cards = $$(".pcard", grid);
    const countEl = $("[data-grid-count]");
    const emptyEl = $("[data-grid-empty]");
    const params = new URL(location.href).searchParams;
    let size = params.get("size") || "";
    const colour = (params.get("colour") || "").toLowerCase();

    const apply = () => {
      tiles.forEach((t) => t.setAttribute("aria-pressed", String(t.dataset.formatTile === size)));
      let shown = 0;
      cards.forEach((card) => {
        const formats = card.dataset.formats || "";
        const colours = card.dataset.colours || "";
        const fHit = !size || (MAP[size] || [size]).some((frag) => formats.includes(frag));
        const cHit = !colour || !card.hasAttribute("data-colours") || colours.includes(colour);
        const hit = fHit && cHit;
        card.hidden = !hit;
        if (hit) shown++;
      });
      if (emptyEl) emptyEl.hidden = shown > 0 || (!size && !colour);
      if (countEl) countEl.textContent = countEl.textContent.replace(/^\d+/, String(shown));
    };

    tiles.forEach((tile) => {
      tile.addEventListener("click", (e) => {
        e.preventDefault();
        const key = tile.dataset.formatTile;
        size = tile.getAttribute("aria-pressed") === "true" ? "" : key;
        const url = new URL(location.href);
        if (size) url.searchParams.set("size", size);
        else url.searchParams.delete("size");
        history.replaceState(null, "", url);
        apply();
      });
    });

    apply();
  };

  /* Favourites strip marquee */
  AMICE.Marquee = function () {
    $$("[data-marquee]").forEach((strip) => {
      if (strip.dataset.marqueeReady) return;
      if (matchMedia("(prefers-reduced-motion: reduce)").matches) return;
      const items = Array.from(strip.children);
      if (items.length < 2 || strip.scrollWidth <= strip.clientWidth + 2) return;
      strip.dataset.marqueeReady = "1";
      const track = document.createElement("div");
      track.className = "marquee__track";
      items.forEach((item) => track.appendChild(item));
      items.forEach((item) => {
        const clone = item.cloneNode(true);
        clone.setAttribute("aria-hidden", "true");
        $$("a", clone).forEach((a) => { a.tabIndex = -1; });
        $$(".reveal", clone).forEach((el) => el.classList.add("in"));
        if (clone.classList.contains("reveal")) clone.classList.add("in");
        $$(".img-fade", clone).forEach((el) => el.classList.add("img-in"));
        track.appendChild(clone);
      });
      strip.appendChild(track);
      strip.classList.add("is-marquee");
      const speed = Math.max(10, +strip.dataset.marqueeSpeed || 40);
      const setDistance = () => {
        const first = track.children[0], firstClone = track.children[items.length];
        const w = firstClone ? firstClone.offsetLeft - first.offsetLeft : track.scrollWidth / 2;
        track.style.setProperty("--marquee-w", w + "px");
        track.style.animationDuration = (w / speed) + "s";
      };
      setDistance();
      window.addEventListener("resize", setDistance, { passive: true });
      const pause = () => { track.style.animationPlayState = "paused"; };
      const run = () => { track.style.animationPlayState = "running"; };
      ["mouseenter", "focusin", "touchstart"].forEach((ev) => strip.addEventListener(ev, pause, { passive: true }));
      ["mouseleave", "focusout", "touchend", "touchcancel"].forEach((ev) => strip.addEventListener(ev, run, { passive: true }));
    });
  };

  /* Menu feature caption contrast */
  AMICE.CaptionContrast = function () {
    $$(".megamenu__feature, .menu__feature").forEach(async (fig) => {
      const img = $("img", fig);
      const cap = $("figcaption", fig);
      if (!img || !cap) return;
      try {
        const blob = await (await fetch(img.currentSrc || img.src)).blob();
        const bmp = await createImageBitmap(blob);
        const c = document.createElement("canvas");
        c.width = 24; c.height = 24;
        const ctx = c.getContext("2d");
        ctx.drawImage(bmp, 0, bmp.height / 2, bmp.width / 2, bmp.height / 2, 0, 0, 24, 24);
        const d = ctx.getImageData(0, 0, 24, 24).data;
        let lum = 0;
        for (let i = 0; i < d.length; i += 4) lum += 0.299 * d[i] + 0.587 * d[i + 1] + 0.114 * d[i + 2];
        lum /= d.length / 4;
        cap.classList.toggle("cap-dark", lum > 150);
      } catch (e) { /* keep the default colour */ }
    });
  };

  /* Pinned feature scroll-stopper */
  AMICE.DetailScroll = function () {
    const root = $("[data-dscroll]");
    if (!root || root.dataset.bound) return;
    root.dataset.bound = "true";
    const items = $$("[data-dscroll-item]", root);
    if (items.length < 2) return;
    const track = $("[data-dscroll-track]", root);
    const segs = $$("[data-dscroll-seg]", root);
    const vids = items.map((it) => $("video", it));
    const reduced = matchMedia("(prefers-reduced-motion: reduce)").matches;
    const desktop = matchMedia("(min-width: 981px)");
    let active = -1;

    const setActive = (i) => {
      if (i === active) return;
      active = i;
      items.forEach((el, j) => { if (j === i) el.setAttribute("data-on", "true"); else el.removeAttribute("data-on"); });
      segs.forEach((s, j) => s.classList.toggle("is-active", j === i));
      vids.forEach((v, j) => {
        if (!v) return;
        if (j === i && !reduced) { const p = v.play(); if (p) p.catch(() => {}); }
        else v.pause();
      });
    };

    const onScroll = () => {
      if (!desktop.matches) return;
      const r = root.getBoundingClientRect();
      const span = r.height - innerHeight;
      if (span <= 0) return;
      const p = Math.min(Math.max(-r.top / span, 0), 0.999);
      setActive(Math.floor(p * items.length));
    };
    addEventListener("scroll", onScroll, { passive: true });

    let t;
    track.addEventListener("scroll", () => {
      if (desktop.matches) return;
      clearTimeout(t);
      t = setTimeout(() => {
        const mid = track.scrollLeft + track.clientWidth / 2;
        let best = 0, bd = Infinity;
        items.forEach((el, j) => { const d = Math.abs(el.offsetLeft + el.offsetWidth / 2 - mid); if (d < bd) { bd = d; best = j; } });
        setActive(best);
      }, 80);
    }, { passive: true });

    segs.forEach((seg, i) => seg.addEventListener("click", () => {
      if (desktop.matches) {
        const span = root.offsetHeight - innerHeight;
        const top = root.getBoundingClientRect().top + scrollY;
        scrollTo({ top: top + (span * (i + 0.5)) / items.length, behavior: reduced ? "auto" : "smooth" });
      } else {
        const el = items[i];
        track.scrollTo({ left: el.offsetLeft - (track.clientWidth - el.offsetWidth) / 2, behavior: reduced ? "auto" : "smooth" });
      }
    }));

    desktop.addEventListener("change", () => { active = -1; setActive(0); onScroll(); });
    setActive(0);
    onScroll();
  };

  AMICE.SectionEvents = function () {
    if (!window.Shopify || !window.Shopify.designMode) return;
    document.addEventListener("shopify:section:load", () => {
      ["Header", "Menu", "CartDrawer", "CartTag", "Product", "Personalise", "Bundle", "Accordion", "Reviews", "Reveal", "Announce", "LocalWx", "DeliveryDate", "LockGuide", "Parallax", "Lightbox", "VideoBand", "FormatFilter", "Marquee", "DetailScroll", "CaptionContrast"].forEach((k) => {
        if (typeof AMICE[k] === "function") AMICE[k]();
      });
    });
    document.addEventListener("shopify:block:select", (e) => {
      const btn = e.target.querySelector("[data-acc-btn]");
      if (btn && btn.getAttribute("aria-expanded") !== "true") btn.click();
    });
  };

  /* ==========================================================================
     INIT
     ========================================================================== */
  document.addEventListener("DOMContentLoaded", () => {
    Object.keys(AMICE).forEach((k) => {
      if (typeof AMICE[k] === "function" && k.charAt(0) === k.charAt(0).toUpperCase()) AMICE[k]();
    });
    const y = document.getElementById("year"); if (y) y.textContent = new Date().getFullYear();
  });
})();
